import { createDefaultWorkspaceDocument, relayablyWorkspaceDocumentSchema } from '@monorepo/relayably/workspaces'
import { WorkspaceResponse } from './types'

const STORAGE_KEY = 'relayably.mock.workspace.state'

type PersistedWorkspaceState = {
  bySession: Record<string, WorkspaceResponse>
}

const readStorage = (): PersistedWorkspaceState => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { bySession: {} }
  }

  const raw = window.localStorage.getItem(STORAGE_KEY)
  return raw ? (JSON.parse(raw) as PersistedWorkspaceState) : { bySession: {} }
}

const writeStorage = (value: PersistedWorkspaceState) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
}

export const mockRelayablyWorkspaceApi = {
  async getCurrentWorkspace(sessionToken: string, profile: { fullName: string; email: string }) {
    const state = readStorage()

    if (!state.bySession[sessionToken]) {
      state.bySession[sessionToken] = {
        workspace: createDefaultWorkspaceDocument({
          email: profile.email,
          fullName: profile.fullName,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
        nextStep: 'workspace-profile',
        billing: {
          customerId: null,
          subscriptionId: null,
          planId: null,
          status: null,
          cancelAtPeriodEnd: false,
          currentPeriodEnd: null,
          canceledAt: null,
        },
        access: {
          state: 'active',
          releasePhoneNumberAt: null,
          disabledAt: null,
          disableReason: null,
        },
        notifications: [],
      }
      writeStorage(state)
    }

    return state.bySession[sessionToken]
  },

  async saveWorkspace(sessionToken: string, workspace: WorkspaceResponse['workspace']) {
    const state = readStorage()
    const validatedWorkspace = relayablyWorkspaceDocumentSchema.parse(workspace)
    state.bySession[sessionToken] = {
      workspace: validatedWorkspace,
      nextStep: validatedWorkspace.onboarding.currentStep,
      billing: state.bySession[sessionToken]?.billing ?? {
        customerId: null,
        subscriptionId: null,
        planId: null,
        status: null,
        cancelAtPeriodEnd: false,
        currentPeriodEnd: null,
        canceledAt: null,
      },
      access: state.bySession[sessionToken]?.access ?? {
        state: 'active',
        releasePhoneNumberAt: null,
        disabledAt: null,
        disableReason: null,
      },
      notifications: state.bySession[sessionToken]?.notifications ?? [],
    }
    writeStorage(state)
    return state.bySession[sessionToken]
  },
}
